// Daten der Mieterliste: Mieter, Objekte, Einheiten und Portal-Profile je einmal (seitenweise)
// geladen, dazu die Mietkonten aller Mieter aus dem gemeinsamen Mietkonto – keine Abfrage je Mieter.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import { alleSeiten } from '../supabase/alleSeiten';
import { ladeMieterKonten, type MieterKontenLaden } from '../mietkonto/api';
import type { ObjektKopfRoh } from '../immobilien/objektKopf';
import type { MieterListenRoh } from './liste';

export interface MieterListeDaten {
  /** vollständige Zeilen (select *) – auch für den Export */
  mieter: (MieterListenRoh & Record<string, unknown>)[];
  objekte: (ObjektKopfRoh & { status?: string | null })[];
  einheiten: { id: string; bezeichnung: string | null; objekt_id: string | null }[];
  /** profiles.status je E-Mail (klein) für Rolle mieter */
  portal: Map<string, string | null>;
  mietkonto: MieterKontenLaden;
}

export type MieterListeZustand =
  | { status: 'laedt' }
  | { status: 'fehler'; meldung: string }
  | { status: 'ok'; daten: MieterListeDaten };

async function lade(heute: string): Promise<MieterListeDaten> {
  const [mieter, objekte, einheiten, profile, mietkonto] = await Promise.all([
    alleSeiten<MieterListeDaten['mieter'][number]>((von, bis) => supabase.from('mieter').select('*').order('nachname').order('id').range(von, bis)),
    alleSeiten<MieterListeDaten['objekte'][number]>((von, bis) => supabase.from('objekte').select('*').eq('ist_verwaltung', false).order('objektnummer').range(von, bis)),
    alleSeiten<MieterListeDaten['einheiten'][number]>((von, bis) => supabase.from('objekt_einheiten').select('id, bezeichnung, objekt_id').order('id').range(von, bis)),
    alleSeiten<{ email: string | null; status: string | null }>((von, bis) => supabase.from('profiles').select('email, status').eq('role', 'mieter').order('id').range(von, bis)),
    ladeMieterKonten(heute),
  ]);
  const portal = new Map<string, string | null>();
  for (const p of profile) {
    const mail = (p.email ?? '').trim().toLowerCase();
    // Mehrere Profile zur selben E-Mail: ein aktives gewinnt
    if (mail && portal.get(mail) !== 'Aktiv') portal.set(mail, p.status);
  }
  return { mieter, objekte, einheiten, portal, mietkonto };
}

export function useMieterListe(heute: string): { zustand: MieterListeZustand; neuLaden: () => void } {
  const [zustand, setZustand] = useState<MieterListeZustand>({ status: 'laedt' });
  const [runde, setRunde] = useState(0);

  useEffect(() => {
    let aktiv = true;
    lade(heute)
      .then(daten => { if (aktiv) setZustand({ status: 'ok', daten }); })
      .catch((e: unknown) => { if (aktiv) setZustand({ status: 'fehler', meldung: e instanceof Error ? e.message : 'Unbekannter Fehler' }); });
    return () => { aktiv = false; };
  }, [heute, runde]);

  const neuLaden = useCallback(() => setRunde(r => r + 1), []);
  return { zustand, neuLaden };
}
